import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookmarkPlusIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
interface SaveTemplateDialogProps {
  open: boolean;
  /** Category label the template will be filed under, or null if none chosen. */
  categoryLabel: string | null;
  defaultName: string;
  onClose: () => void;
  onSave: (name: string, description: string) => void;
}

/** Small modal asking for a name and description before saving a template. */
export function SaveTemplateDialog({
  open,
  categoryLabel,
  defaultName,
  onClose,
  onSave
}: SaveTemplateDialogProps) {
  const {
    t
  } = useLanguage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setDescription('');
    const id = window.setTimeout(() => inputRef.current?.select(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, defaultName, onClose]);
  const canSave = Boolean(name.trim()) && Boolean(categoryLabel);
  return <AnimatePresence data-id="element-4147" data-mp="1xn7te1w0c51q">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} data-id="element-4148" data-mp="1102xhcsmpn20">
          <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" data-id="element-4149" data-mp="19y5365nb53vm" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="save-template-title" className="relative w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-xl" initial={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} data-id="element-4150" data-mp="i9ldpxpry1ss">
            <form onSubmit={e => {
          e.preventDefault();
          if (canSave) onSave(name.trim(), description.trim());
        }} data-id="element-4151" data-mp="1uc16cb49spj7">
              <div className="flex items-start justify-between gap-4 px-6 pt-5" data-id="element-4152" data-mp="16o9vlbydokk1">
                <div className="flex items-center gap-3" data-id="element-4153" data-mp="11p6t9z9s8sfr">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-indigo-50 text-[#4F46E5]" data-id="element-4154" data-mp="1fnddiclt6twy">
                    <BookmarkPlusIcon className="w-4 h-4" data-id="element-4155" data-mp="1bjyowxjt157w" />
                  </span>
                  <div data-id="element-4156" data-mp="2r18rvj4w2yn4">
                    <h2 id="save-template-title" className="text-base font-semibold text-[#0F172A]" data-id="element-4157" data-mp="2r0h0kkibrn2n">
                      {t('Enregistrer comme modèle', 'Save as template')}
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-4158" data-mp="2zkc5x93ty7fm">
                      {categoryLabel ? t(`Classé dans « ${categoryLabel} »`, `Filed under “${categoryLabel}”`) : t('Choisissez d’abord une catégorie pour l’avis.', 'Pick a category for the notice first.')}
                    </p>
                  </div>
                </div>
                <button type="button" onClick={onClose} aria-label={t('Fermer', 'Close')} className="p-1.5 -mr-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-4159" data-mp="38jz9a0g4dnbb">
                  <XIcon className="w-4 h-4" data-id="element-4160" data-mp="4wqk8gh82cum" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4" data-id="element-4161" data-mp="2d9vomno05tkl">
                <div data-id="element-4162" data-mp="28clyf5sshdvp">
                  <label htmlFor="tpl-name" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4163" data-mp="28bamgip6wzti">
                    {t('Nom du modèle', 'Template name')}
                  </label>
                  <input ref={inputRef} id="tpl-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t('Ex. : Fermeture de rue', 'E.g. Street closure')} className="block w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4164" data-mp="2w26m21zw0zbb" />
                </div>
                <div data-id="element-4165" data-mp="3a1z5rdczyf9s">
                  <label htmlFor="tpl-desc" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4166" data-mp="3a0o5hnirfpnf">
                    {t('Description', 'Description')}{' '}
                    <span className="font-normal text-slate-400" data-id="element-4167" data-mp="3meji2wn72alh">
                      {t('(facultatif)', '(optional)')}
                    </span>
                  </label>
                  <input id="tpl-desc" type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder={t('Quand l’utiliser', 'When to use it')} className="block w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4168" data-mp="6tmbecamjpg" />
                </div>
              </div>

              <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100" data-id="element-4169" data-mp="nwnwfo589u60">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4170" data-mp="sufs8t8zu0fh">
                  {t('Annuler', 'Cancel')}
                </button>
                <button type="submit" disabled={!canSave} className="px-4 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-4171" data-mp="84vcju2731pm">
                  {t('Enregistrer le modèle', 'Save template')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}